import { actionTextColor, readableText } from "../../utils/themeContrast";
import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { IoIosAdd } from "react-icons/io";
import { IoTrashOutline } from "react-icons/io5";
import { UserContext, ThemeContext } from "../../contexts/Context";
import { getMenuAvailabilityInfo } from "../../utils/menuAvailability";

const ItemSize3 = ({
  lanIndex,
  isLanReverse,
  item,
  shouldHide,
  isSetMeal,
  selectedItems,
  currencySymbol,
  addItemDirect,
  addQty,
  minusQty
}) => {
  const themeContext = React.useContext(ThemeContext);
  const {
    displayNetAmount,
    tax,
    hideLimitedTimeInfo,
    displayMenuDescription,
    hidePrice,
    isMenuView
  } = React.useContext(UserContext);
  const imageRef = useRef(null);
  const availabilityInfo = hideLimitedTimeInfo ? null : getMenuAvailabilityInfo(item);
  const shouldDisplayDescription = displayMenuDescription === true || displayMenuDescription === "true";
  const title = (lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse)
    ? (item.isCategory ? item.category : item.title)
    : (item.isCategory ? item.category2 : (item.title2 || item.title));
  const description = (lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse)
    ? item.description
    : (item.description2 || item.description);
  const quantity = selectedItems && selectedItems[item.codeId];

  useEffect(() => {
    if (!shouldHide || !imageRef.current) return;
    if (imageRef.current.getBoundingClientRect().top < window.innerHeight) {
      imageRef.current.style.backgroundImage = `url('${imageRef.current.dataset.bg}')`;
      imageRef.current.classList.remove("item-img");
    }
  }, [item.image, shouldHide]);

  const handleAction = (event, action) => {
    event.stopPropagation();
    if (!item.isSoldOut && action) action(event);
  };

  return (
    <ItemWrapper themeContext={themeContext}>
      <ItemDetails>
        <ItemTitle themeContext={themeContext}>{title}</ItemTitle>
        {!hidePrice && item.price > 0 &&
          <ItemPriceGroup>
            <PriceDisplay themeContext={themeContext}>
              {`${isSetMeal ? "+" : ""}${currencySymbol} ${(displayNetAmount ? (item.price * (1 + (tax.taxMode == 0 ? tax.tax : item.tax))) : item.price).toFixed(2)}`}
            </PriceDisplay>
          </ItemPriceGroup>
        }
        {shouldDisplayDescription && description &&
          <ItemDescription themeContext={themeContext}>{description}</ItemDescription>
        }
        {availabilityInfo &&
          <ItemAvailabilityBadge isUnavailable={availabilityInfo.isUnavailable}>
            {availabilityInfo.text}
          </ItemAvailabilityBadge>
        }
      <ActionArea>
        {!isMenuView && !quantity &&
          <AddButton
            type="button"
            themeContext={themeContext}
            disabled={item.isSoldOut}
            onClick={(event) => handleAction(event, addQty)}
            aria-label="Add item"
          >
            <IoIosAdd size={28} />
          </AddButton>
        }
        {!isMenuView && quantity && addItemDirect &&
          <QuantityControl>
            <QuantityButton type="button" disabled={item.isSoldOut} aria-label="Remove one item" onClick={(event) => handleAction(event, minusQty)}>
              <IoTrashOutline size={18} aria-hidden="true" />
            </QuantityButton>
            <QuantityText themeContext={themeContext}>{quantity}</QuantityText>
            <AddButton themeContext={themeContext} type="button" disabled={item.isSoldOut} aria-label="Add item" onClick={(event) => handleAction(event, addQty)}>
              <IoIosAdd size={20} />
            </AddButton>
          </QuantityControl>
        }
        {!isMenuView && quantity && !addItemDirect &&
          <QuantityBadge themeContext={themeContext}>
            &times;{quantity}
          </QuantityBadge>
        }
      </ActionArea>
      </ItemDetails>
      <ImageArea>
        <ItemImage
          ref={imageRef}
          className="item-img"
          data-bg={item.image}
          shouldHide={shouldHide}
        />
        {item.isSoldOut && <SoldOut src="/soldout.png" />}
      </ImageArea>
    </ItemWrapper>
  );
};

export default ItemSize3;

const ItemWrapper = styled.div`
  --menu-item-font-size: 15px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  width: 100%;
  box-sizing: border-box;
  gap: 4px 10px;
  padding: 10px;
  border-radius: 14px;
  background-color: ${(props) => props.themeContext.backgroundColor || props.themeContext.optionHeaderBackgroundColor};
  @media (min-width: 768px) {
    --menu-item-font-size: 16px;
    gap: 4px 12px;
    padding: 10px 12px;
  }

  @media (max-width: 360px) {
    gap: 8px;
    padding: 8px;
  }
`;

const ItemDetails = styled.div`
  grid-column: 2;
  grid-row: 1;
  align-self: stretch;
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  > * { flex-shrink: 0; }
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const ItemTitle = styled.div`
  display: -webkit-box;
  color: ${(props) => readableText(props.themeContext.backgroundColor, props.themeContext.menuItemNameColor, props.themeContext.primaryColor)};
  font-family: ${(props) => props.themeContext.fontFamily};
  font-size: var(--menu-item-font-size);
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;

  @media (max-width: 360px) {
    font-size: 15px;
  }
`;

const ItemDescription = styled.div`
  display: -webkit-box;
  margin-top: 6px;
  color: ${(props) => props.themeContext.descriptionColor || props.themeContext.fontSecondary};
  font-family: ${(props) => props.themeContext.fontFamily};
  font-size: 13px;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  @media (min-width: 768px) { font-size: 16px; }

  @media (max-width: 360px) {
    margin-top: 6px;
    font-size: 13px;
  }
`;

const ItemPriceGroup = styled.div`
  display: flex;
  margin-top: 4px;
  flex-shrink: 0;
  min-height: 20px;
  flex-direction: column;
  justify-content: flex-start;
  text-align: left;
  white-space: nowrap;

  @media (min-width: 768px) {
    min-height: 24px;
    justify-content: center;
  }
`;

const PriceDisplay = styled.div`
  color: ${(props) => readableText(props.themeContext.backgroundColor, props.themeContext.menuItemPriceColor, props.themeContext.fontPrice, "#a3000a")};
  font-family: ${(props) => props.themeContext.fontFamily};
  font-size: var(--menu-item-font-size);
  font-weight: 700;
  line-height: 1.3;
`;

const ItemAvailabilityBadge = styled.div`
  display: inline-block;
  max-width: 100%;
  margin-top: 8px;
  padding: 3px 8px;
  border-radius: 4px;
  color: ${(props) => props.isUnavailable ? "#9a4b00" : "#0f6b35"};
  background-color: ${(props) => props.isUnavailable ? "rgba(255, 152, 0, 0.14)" : "rgba(29, 145, 74, 0.14)"};
  border: 1px solid ${(props) => props.isUnavailable ? "rgba(255, 152, 0, 0.45)" : "rgba(29, 145, 74, 0.45)"};
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ImageArea = styled.div`
  grid-column: 1;
  grid-row: 1;
  position: relative;
  width: 112px;
  height: 112px;
  @media (min-width: 768px) { width: 128px; height: 128px; }

  @media (max-width: 360px) {
    width: 100px;
    height: 100px;
  }
`;

const ItemImage = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 10px;
  background-image: url(${(props) => props.shouldHide ? "" : props["data-bg"]});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  overflow: hidden;
`;

const ActionArea = styled.div`
  width: 100%;
  margin-top: auto;
  padding-top: 6px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  min-width: 0;
  &:empty { display: none; }
`;

const AddButton = styled.button`
  display: flex;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  color: ${props => actionTextColor(props.themeContext)};
  background: ${props => props.themeContext.primaryColor};
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  &:disabled { cursor: not-allowed; opacity: 0.5; }
  &:focus-visible { outline: 2px solid ${props => props.themeContext.primaryColor}; outline-offset: 2px; }
  @media (min-width: 768px) { width: 34px; height: 34px; }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const QuantityButton = styled.button`
  display: flex;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid #efb4af;
  border-radius: 50%;
  color: #c62828;
  background: #f8e9e7;
  cursor: pointer;
  &:disabled { cursor: not-allowed; opacity: 0.5; }
  &:focus-visible { outline: 2px solid #c62828; outline-offset: 2px; }
  @media (min-width: 768px) { width: 34px; height: 34px; }
`;

const QuantityText = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 28px;
  height: 28px;
  padding: 0 4px;
  border: 1px solid #333;
  border-radius: 999px;
  color: #111;
  background: #f7f7f7;
  font-family: ${props => props.themeContext.fontFamily};
  font-size: 15px;
  font-weight: 600;
  @media (min-width: 768px) { min-width: 34px; height: 34px; }
`;

const QuantityBadge = styled(QuantityText)`
  color: ${props => actionTextColor(props.themeContext)};
  background: ${props => props.themeContext.primaryColor};
  border-color: ${props => props.themeContext.primaryColor};
`;
const SoldOut = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 90%;
  transform: translate(-50%, -50%);
`;
